import { useState } from 'react';

export default function ContractorBenefits() {
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    serviceType: '',
    yearsInBusiness: '',
    licenseNumber: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5u4mdpr44f5krorkk60', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          businessName: '',
          contactName: '',
          email: '',
          phone: '',
          serviceType: '',
          yearsInBusiness: '',
          licenseNumber: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="list-service" className="py-20 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contractors: Grow Your Business with Us
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Join El Paso's premier contractor directory and connect with thousands of homeowners actively searching for your services. Our platform helps qualified contractors generate leads, build their reputation, and grow their business in the local market.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-12">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of Listing Your Service
            </h3>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0">
                  <i className="ri-user-search-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    Qualified Lead Generation
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Connect with homeowners who are actively searching for contractors in your specialty. Our platform attracts serious customers ready to hire, not just browsers. Receive inquiries from pre-qualified leads who understand the value of professional services and are prepared to invest in quality work.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0">
                  <i className="ri-trophy-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    Build Your Reputation
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Showcase your best work through a professional profile featuring project photos, customer reviews, certifications, and specialties. Positive reviews and completed projects build trust with potential clients and differentiate you from competitors. Our verified review system ensures authentic feedback that strengthens your credibility.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0">
                  <i className="ri-line-chart-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    Increase Visibility
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Appear in search results when homeowners look for contractors in your service area and specialty. Featured listings receive premium placement and up to 10x more profile views. Our SEO-optimized platform ranks highly in Google searches for El Paso contractors, driving organic traffic to your profile.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    Verified Professional Status
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Our vetting process validates your credentials, giving customers confidence in your professionalism. Display badges for licensing, insurance, certifications, and years of experience. Being part of a trusted directory immediately elevates your credibility and helps you stand out in a crowded market.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    Cost-Effective Marketing
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Reach thousands of potential customers for a fraction of traditional advertising costs. No expensive billboards, radio ads, or direct mail campaigns needed. Our subscription-based model provides predictable marketing costs with measurable ROI. Track inquiries, profile views, and conversion rates through your contractor dashboard.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0">
                  <i className="ri-customer-service-2-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    Dedicated Support
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Our team helps you optimize your profile, respond to reviews, and maximize your listing's effectiveness. We provide guidance on photography, description writing, and best practices for converting inquiries into jobs. Access marketing resources, industry insights, and networking opportunities with other professionals.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              List Your Service Today
            </h3>
            <form id="contractor-listing-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="businessName"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  placeholder="Your Company Name"
                />
              </div>

              <div>
                <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  placeholder="Your Full Name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  placeholder="(915) 555-0123"
                />
              </div>

              <div>
                <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-1">
                  Service Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="serviceType"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                >
                  <option value="">Select a service</option>
                  <option value="roofing">Roofing</option>
                  <option value="hvac">HVAC</option>
                  <option value="plumbing">Plumbing</option>
                  <option value="remodeling">Remodeling</option>
                  <option value="landscaping">Landscaping</option>
                  <option value="concrete">Concrete</option>
                  <option value="pool">Pool Installation</option>
                  <option value="solar">Solar Installation</option>
                  <option value="electrical">Electrical</option>
                  <option value="painting">Painting</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="yearsInBusiness" className="block text-sm font-medium text-gray-700 mb-1">
                  Years in Business <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="yearsInBusiness"
                  name="yearsInBusiness"
                  value={formData.yearsInBusiness}
                  onChange={handleChange}
                  required
                  min="0"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  placeholder="5"
                />
              </div>

              <div>
                <label htmlFor="licenseNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  License Number
                </label>
                <input
                  type="text"
                  id="licenseNumber"
                  name="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  placeholder="TX-123456"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Tell Us About Your Business
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm resize-none"
                  placeholder="Brief description of your services and what makes your business unique..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    Thank you! We'll review your information and contact you within 1-2 business days.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    Something went wrong. Please try again or contact us directly.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
